import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Divider, Row, Select, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';

const { Title } = Typography;
const { Option } = Select;

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function monthKeyMinus(n) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function generateMonths() {
  const months = [];
  const now = new Date();
  for (let i = -24; i <= 0; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months.sort().reverse();
}

function fmtMonth(mk) {
  if (!mk) return mk;
  const [y, m] = mk.split('-');
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${labels[parseInt(m, 10) - 1]} ${y}`;
}

const MONTHS = generateMonths();

// Separation type. Laid off is involuntary -- the employer initiates it, the
// employee does not choose to leave. Defined once and used by the scorecards,
// the monthly pivot and both tables so the classification cannot drift between
// them; the matching SQL filter lives in _term_type_clause in routes/census.py.
// Tenure from days employed, e.g. "1y 3m", or days under a month.
const fmtTenureDays = (days) => {
  if (days == null) return '—';
  if (days < 31) return `${days}d`;
  const yrs = Math.floor(days / 365.25);
  const mos = Math.floor((days - yrs * 365.25) / 30.44);
  return yrs > 0 ? `${yrs}y ${mos}m` : `${mos}m`;
};

const volCount   = r => (r.Resigned ?? 0) + (r.Retired ?? 0);
const involCount = r => (r.Terminated ?? 0) + (r.LaidOff ?? 0);

/**
 * `embedded` renders without the heading, filters or Search button, scoped by
 * the team and window passed in, and `sections` picks which tables to draw.
 * The Campaign Summary's Census tab asks for the Detail table alone.
 *
 * sections: 'summary' | 'detail' | 'reason'. Omitted means all of them.
 */
export default function AttritionPage({ embedded = false, teamId = null, from = null, to = null, sections = null }) {
  const currentUser = useContext(UserContext);

  const [teams,           setTeams]           = useState([]);
  // Initialised from the prop, not defaulted and then corrected. An empty
  // array means no team filter at all -- every team -- so a fetch that beat
  // the correcting effect asked the widest possible question.
  const [selectedTeamIds, setSelectedTeamIds] = useState(embedded && teamId ? [teamId] : []);
  const [selectedJobs,    setSelectedJobs]    = useState([]);
  const [termType,        setTermType]        = useState(undefined); // undefined = all
  // The window arrives as dates and this page works in YYYY-MM month keys,
  // so the first seven characters are the conversion.
  const [fromMonth,       setFromMonth]       = useState(() => (embedded && from ? from.slice(0, 7) : monthKeyMinus(2)));
  const [toMonth,         setToMonth]         = useState(() => (embedded && to ? to.slice(0, 7) : currentMonthKey()));
  const [data,            setData]            = useState([]);
  const [reasonData,      setReasonData]      = useState([]);
  const [listData,        setListData]        = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [searched,        setSearched]        = useState(false);

  const show = (name) => !sections || sections.includes(name);
  // A plain boolean so the search callback stays stable: the embedded caller
  // passes a fresh sections array on every render.
  const wantList = show('list');

  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then(r => setTeams(r.data || []));
  }, []);

  // Requests are not cancelled, so two can be in flight after a quick filter
  // change and the slower one would win on arrival. Only the newest may write.
  const reqSeq = useRef(0);

  const handleSearch = useCallback(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setSearched(true);
    const params = {
      from_month: fromMonth,
      to_month:   toMonth,
      team_ids:   selectedTeamIds.length ? selectedTeamIds.join(',') : undefined,
      // Filtered in SQL so the tenure buckets and average tenure are computed
      // over the same set as the head counts
      term_type:  termType,
    };
    Promise.all([
      api.get('/api/census/attrition',           { params }),
      api.get('/api/census/attrition-by-reason', { params }),
      wantList ? api.get('/api/census/attrition-list', { params }) : Promise.resolve({ data: [] }),
    ])
      .then(([r1, r2, r3]) => {
        if (seq !== reqSeq.current) return;
        setData(r1.data || []); setReasonData(r2.data || []); setListData(r3.data || []);
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [fromMonth, toMonth, selectedTeamIds, termType, wantList]);

  // Embedded there is no Search button, so the search runs from the props.
  // The team and window are set first and the fetch follows them, rather than
  // firing once against the page's own defaults.
  useEffect(() => {
    if (!embedded) return;
    setSelectedTeamIds(teamId ? [teamId] : []);
    if (from) setFromMonth(from.slice(0, 7));
    if (to)   setToMonth(to.slice(0, 7));
  }, [embedded, teamId, from, to]);

  // Only once the state agrees with the props. Setting it above is queued
  // while this effect runs in the same commit, so without the check the first
  // search went out with the previous render's team -- and on mount that is
  // none, which the API reads as all of them.
  useEffect(() => {
    if (!embedded) return;
    const teamReady = teamId
      ? (selectedTeamIds.length === 1 && selectedTeamIds[0] === teamId)
      : selectedTeamIds.length === 0;
    if (!teamReady) return;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, teamId, selectedTeamIds, handleSearch]);

  // ── Job options derived from fetched data ──────────────────────────────────
  const jobOptions = useMemo(() =>
    [...new Set(data.map(r => r.JobName).filter(Boolean))].sort()
      .map(j => ({ label: j, value: j })),
  [data]);

  // ── Apply job filter client-side ────────────────────────────────────────────
  const filteredData = useMemo(() =>
    selectedJobs.length ? data.filter(r => selectedJobs.includes(r.JobName)) : data,
  [data, selectedJobs]);

  // Employee Attrition List: same Job filter as the tables above.
  const listRows = useMemo(() =>
    (selectedJobs.length ? listData.filter(r => selectedJobs.includes(r.JobName)) : listData)
      .map((r, i) => ({ ...r, _key: i })),
  [listData, selectedJobs]);
  const canViewSummary = (currentUser?.CanViewAgentSummary ?? 0) >= 1;
  const listColumns = [
    { title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName', fixed: 'left',
      sorter: (a, b) => (a.EmployeeName || '').localeCompare(b.EmployeeName || ''),
      render: (v, r) => (canViewSummary && r.UserID
        ? <Link to={`/agents?user_id=${r.UserID}`}>{v}</Link>
        : (v || '—')) },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName',
      sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || '') },
    { title: 'Job', dataIndex: 'JobName', key: 'JobName',
      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || ''),
      render: v => v || '—' },
    { title: 'Tenure', dataIndex: 'TenureDays', key: 'TenureDays', align: 'right',
      sorter: (a, b) => (a.TenureDays ?? -1) - (b.TenureDays ?? -1),
      render: fmtTenureDays },
    { title: 'Term Date', dataIndex: 'TermDate', key: 'TermDate',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.TermDate || '').localeCompare(b.TermDate || '') },
    { title: 'Term Reason', dataIndex: 'TermReason', key: 'TermReason',
      sorter: (a, b) => (a.TermReason || '').localeCompare(b.TermReason || ''),
      render: v => v || '—' },
  ];

  // ── Summary totals ──────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const tot = filteredData.reduce((a, r) => ({
      total:      a.total      + r.TotalTerms,
      voluntary:  a.voluntary   + volCount(r),
      involuntary:a.involuntary + involCount(r),
      tenureSum:  a.tenureSum  + r.AvgTenureDays * r.TotalTerms,
      t0to18:    a.t0to18    + r.Terms0to18,
      t19to60:   a.t19to60   + r.Terms19to60,
      t61to180:  a.t61to180  + r.Terms61to180,
      t181to365: a.t181to365 + r.Terms181to365,
      t366to730: a.t366to730 + r.Terms366to730,
      t731plus:  a.t731plus  + r.Terms731plus,
    }), { total: 0, voluntary: 0, involuntary: 0, tenureSum: 0, t0to18: 0, t19to60: 0, t61to180: 0, t181to365: 0, t366to730: 0, t731plus: 0 });

    return {
      ...tot,
      avgTenure: tot.total ? Math.round(tot.tenureSum / tot.total) : 0,
    };
  }, [filteredData]);

  // ── Months in result (ascending) ───────────────────────────────────────────
  const pivotMonths = useMemo(() =>
    [...new Set(filteredData.map(r => r.MonthKey))].sort(), [filteredData]);

  // ── Pivot: rows = Team, columns = months ───────────────────────────────────
  const pivotRows = useMemo(() => {
    const teamMap = {};
    filteredData.forEach(r => {
      if (!teamMap[r.TeamID]) {
        teamMap[r.TeamID] = { TeamID: r.TeamID, TeamName: r.TeamName, _totalTerms: 0, _totalVol: 0, _totalInvol: 0 };
      }
      const t = teamMap[r.TeamID];
      t._totalTerms += r.TotalTerms;
      t._totalVol   += volCount(r);
      t._totalInvol += involCount(r);
      const cell = t[r.MonthKey] || { total: 0, voluntary: 0, involuntary: 0, t0: 0, t19: 0, t61: 0, t181: 0, t366: 0, t731: 0 };
      cell.total       += r.TotalTerms;
      cell.voluntary   += volCount(r);
      cell.involuntary += involCount(r);
      cell.t0          += r.Terms0to18;
      cell.t19         += r.Terms19to60;
      cell.t61         += r.Terms61to180;
      cell.t181        += r.Terms181to365;
      cell.t366        += r.Terms366to730;
      cell.t731        += r.Terms731plus;
      t[r.MonthKey] = cell;
    });
    return Object.values(teamMap).sort((a, b) => a.TeamName.localeCompare(b.TeamName));
  }, [data]);

  // ── Detail table rows: one per Team+Job+Month ──────────────────────────────
  const detailRows = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) =>
      b.MonthKey.localeCompare(a.MonthKey) || a.TeamName.localeCompare(b.TeamName)
    ).map((r, i) => ({ ...r, _key: i }));
    return sorted.length ? [...sorted, { ...buildTotalRow(sorted), _key: '__total__', _isTotal: true, TeamName: 'Total', MonthKey: '', JobName: '' }] : [];
  }, [filteredData]);

  // ── Pivot columns ───────────────────────────────────────────────────────────
  const pivotColumns = [
    { title: 'Team', dataIndex: 'TeamName', key: 'team', fixed: 'left', width: 200,
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName) },
    { title: 'Total', key: '_total', width: 90, align: 'right', fixed: 'left',
      sorter: (a, b) => a._totalTerms - b._totalTerms,
      render: (_, row) => (
        <div style={{ lineHeight: 1.5, fontSize: 12 }}>
          <div><strong style={{ fontSize: 14 }}>{row._totalTerms}</strong></div>
          <div style={{ color: '#52c41a' }}>Vol: {row._totalVol}</div>
          <div style={{ color: '#ff4d4f' }}>Invol: {row._totalInvol}</div>
        </div>
      ) },
    ...pivotMonths.map(m => ({
      title: fmtMonth(m),
      key: `m_${m}`,
      width: 140,
      align: 'center',
      render: (_, row) => {
        const c = row[m];
        if (!c || !c.total) return <span style={{ color: '#ccc' }}>—</span>;
        return (
          <div style={{ lineHeight: 1.5, fontSize: 12 }}>
            <div><strong>{c.total}</strong> terms</div>
            <div style={{ color: '#52c41a' }}>Vol: {c.voluntary}</div>
            <div style={{ color: '#ff4d4f' }}>Invol: {c.involuntary}</div>
            <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 2, paddingTop: 2, color: '#c0392b' }}>0-18d: {c.t0}</div>
            <div style={{ color: '#c0392b' }}>19-60d: {c.t19}</div>
            <div style={{ color: '#e67e22' }}>61-180d: {c.t61}</div>
            <div style={{ color: '#52c41a' }}>181d-1yr: {c.t181}</div>
            <div style={{ color: '#13c2c2' }}>1yr-2yr: {c.t366}</div>
            <div style={{ color: '#722ed1' }}>2yr+: {c.t731}</div>
          </div>
        );
      },
    })),
  ];

  // ── Detail columns ──────────────────────────────────────────────────────────
  const detailColumns = [
    { title: 'Month',        dataIndex: 'MonthKey',     key: 'MonthKey',     width: 100,
      render: (v, r) => r._isTotal ? <strong>Total</strong> : fmtMonth(v),
      sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') },
    { title: 'Team',         dataIndex: 'TeamName',     key: 'TeamName',     width: 180,
      render: (v, r) => r._isTotal ? null : v,
      sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
    { title: 'Job',          dataIndex: 'JobName',      key: 'JobName',      width: 150,
      render: (v, r) => r._isTotal ? null : (v || '—'),
      sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? '') },
    { title: 'Total Terms',  dataIndex: 'TotalTerms',   key: 'TotalTerms',   width: 110, align: 'right',
      sorter: (a, b) => a.TotalTerms - b.TotalTerms,
      render: (v, r) => <Tag color="default" style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> },
    { title: 'Voluntary',    key: 'Voluntary',           width: 100, align: 'right',
      sorter: (a, b) => volCount(a) - volCount(b),
      render: (_, r) => {
        const v = volCount(r);
        return v ? <Tag color="green" style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> : '—';
      }},
    { title: 'Involuntary',  key: 'Involuntary',         width: 110, align: 'right',
      sorter: (a, b) => involCount(a) - involCount(b),
      render: (_, r) => {
        const v = involCount(r);
        return v ? <Tag color="red" style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> : '—';
      }},
    { title: 'Terms 0-18d',    dataIndex: 'Terms0to18',   key: 'Terms0to18',   width: 105, align: 'right',
      sorter: (a, b) => a.Terms0to18 - b.Terms0to18 },
    { title: 'Terms 19-60d',  dataIndex: 'Terms19to60',  key: 'Terms19to60',  width: 110, align: 'right',
      sorter: (a, b) => a.Terms19to60 - b.Terms19to60 },
    { title: 'Terms 61-180d', dataIndex: 'Terms61to180', key: 'Terms61to180', width: 115, align: 'right',
      sorter: (a, b) => a.Terms61to180 - b.Terms61to180 },
    { title: 'Terms 181d-1yr',dataIndex: 'Terms181to365',key: 'Terms181to365',width: 120, align: 'right',
      sorter: (a, b) => a.Terms181to365 - b.Terms181to365 },
    { title: 'Terms 1yr-2yr', dataIndex: 'Terms366to730',key: 'Terms366to730',width: 115, align: 'right',
      sorter: (a, b) => a.Terms366to730 - b.Terms366to730 },
    { title: 'Terms 2yr+',    dataIndex: 'Terms731plus', key: 'Terms731plus', width: 105, align: 'right',
      sorter: (a, b) => a.Terms731plus - b.Terms731plus },
    { title: 'Avg Tenure',    dataIndex: 'AvgTenureDays',key: 'AvgTenure',    width: 100, align: 'right',
      sorter: (a, b) => a.AvgTenureDays - b.AvgTenureDays,
      render: (v, r) => r._isTotal ? '—' : (v != null ? `${v}d` : '—') },
  ];

  // ── Shared total-row builder ────────────────────────────────────────────────
  function buildTotalRow(rows) {
    return {
      _key: '__total__',
      _isTotal: true,
      TotalTerms:   rows.reduce((s, r) => s + r.TotalTerms,   0),
      Resigned:     rows.reduce((s, r) => s + r.Resigned,     0),
      Retired:      rows.reduce((s, r) => s + r.Retired,      0),
      LaidOff:      rows.reduce((s, r) => s + r.LaidOff,      0),
      Terminated:   rows.reduce((s, r) => s + r.Terminated,   0),
      Terms0to18:   rows.reduce((s, r) => s + r.Terms0to18,   0),
      Terms19to60:  rows.reduce((s, r) => s + r.Terms19to60,  0),
      Terms61to180: rows.reduce((s, r) => s + r.Terms61to180, 0),
      Terms181to365: rows.reduce((s, r) => s + r.Terms181to365, 0),
      Terms366to730: rows.reduce((s, r) => s + r.Terms366to730, 0),
      Terms731plus:  rows.reduce((s, r) => s + r.Terms731plus,  0),
    };
  }

  // ── Reason chart: total terms per reason (all months summed) ─────────────────
  const reasonChartData = useMemo(() => {
    const map = {};
    reasonData.forEach(r => {
      map[r.TermReason] = (map[r.TermReason] || 0) + r.TotalTerms;
    });
    return Object.entries(map)
      .map(([reason, total]) => ({ reason, total }))
      .sort((a, b) => b.total - a.total);
  }, [reasonData]);

  // ── Reason table rows ───────────────────────────────────────────────────────
  const reasonRows = useMemo(() => {
    const sorted = [...reasonData].sort((a, b) =>
      b.MonthKey.localeCompare(a.MonthKey) || a.TermReason.localeCompare(b.TermReason)
    ).map((r, i) => ({ ...r, _key: i }));
    return sorted.length ? [...sorted, { ...buildTotalRow(sorted), TeamName: 'Total', TermReason: 'Total' }] : [];
  }, [reasonData]);

  // ── Reason columns ──────────────────────────────────────────────────────────
  const reasonColumns = [
    { title: 'Month',        dataIndex: 'MonthKey',    key: 'MonthKey',    width: 100,
      render: (v, r) => r._isTotal ? <strong>Total</strong> : fmtMonth(v),
      sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') },
    { title: 'Term Reason',  dataIndex: 'TermReason',  key: 'TermReason',  width: 200, ellipsis: true,
      render: (v, r) => r._isTotal ? null : (v || '—'),
      sorter: (a, b) => (a.TermReason ?? '').localeCompare(b.TermReason ?? '') },
    { title: 'Total Terms',  dataIndex: 'TotalTerms',  key: 'TotalTerms',  width: 110, align: 'right',
      sorter: (a, b) => a.TotalTerms - b.TotalTerms,
      render: (v, r) => <Tag color={r._isTotal ? 'default' : 'default'} style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> },
    { title: 'Voluntary',    key: 'Voluntary',          width: 100, align: 'right',
      sorter: (a, b) => volCount(a) - volCount(b),
      render: (_, r) => { const v = volCount(r); return v ? <Tag color="green" style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> : '—'; } },
    { title: 'Involuntary',  key: 'Involuntary',        width: 110, align: 'right',
      sorter: (a, b) => involCount(a) - involCount(b),
      render: (_, r) => { const v = involCount(r); return v ? <Tag color="red" style={r._isTotal ? { fontWeight: 700 } : {}}>{v}</Tag> : '—'; } },
    { title: 'Terms 0-18d',    dataIndex: 'Terms0to18',   key: 'Terms0to18',   width: 105, align: 'right',
      sorter: (a, b) => a.Terms0to18 - b.Terms0to18 },
    { title: 'Terms 19-60d',  dataIndex: 'Terms19to60',  key: 'Terms19to60',  width: 110, align: 'right',
      sorter: (a, b) => a.Terms19to60 - b.Terms19to60 },
    { title: 'Terms 61-180d', dataIndex: 'Terms61to180', key: 'Terms61to180', width: 115, align: 'right',
      sorter: (a, b) => a.Terms61to180 - b.Terms61to180 },
    { title: 'Terms 181d-1yr',dataIndex: 'Terms181to365',key: 'Terms181to365',width: 120, align: 'right',
      sorter: (a, b) => a.Terms181to365 - b.Terms181to365 },
    { title: 'Terms 1yr-2yr', dataIndex: 'Terms366to730',key: 'Terms366to730',width: 115, align: 'right',
      sorter: (a, b) => a.Terms366to730 - b.Terms366to730 },
    { title: 'Terms 2yr+',    dataIndex: 'Terms731plus', key: 'Terms731plus', width: 105, align: 'right',
      sorter: (a, b) => a.Terms731plus - b.Terms731plus },
  ];

  const fromOptions = MONTHS.filter(m => m <= toMonth);
  const toOptions   = MONTHS.filter(m => m >= fromMonth);

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>Attrition</Title>
      )}

      {/* ── Filters ── */}
      {!embedded && (
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <Select
          mode="multiple"
          value={selectedTeamIds}
          onChange={setSelectedTeamIds}
          style={{ minWidth: 240 }}
          placeholder="All teams"
          optionFilterProp="children"
          maxTagCount="responsive"
          allowClear
        >
          {teams.map(t => <Option key={t.ID} value={t.ID}>{t.Name}</Option>)}
        </Select>

        <Select
          mode="multiple"
          value={selectedJobs}
          onChange={setSelectedJobs}
          style={{ minWidth: 200 }}
          placeholder="All jobs"
          optionFilterProp="label"
          maxTagCount="responsive"
          allowClear
          options={jobOptions}
          disabled={!data.length}
        />

        <Select
          value={termType}
          onChange={setTermType}
          style={{ width: 150 }}
          placeholder="All terms"
          allowClear
          options={[
            { label: 'Voluntary',   value: 'voluntary'   },
            { label: 'Involuntary', value: 'involuntary' },
          ]}
        />

        <Select value={fromMonth} onChange={v => { setFromMonth(v); if (v > toMonth) setToMonth(v); }} style={{ width: 130 }}>
          {fromOptions.map(m => <Option key={m} value={m}>{fmtMonth(m)}</Option>)}
        </Select>
        <span style={{ color: 'var(--ant-color-text-secondary)', fontSize: 13 }}>to</span>
        <Select value={toMonth} onChange={v => { setToMonth(v); if (v < fromMonth) setFromMonth(v); }} style={{ width: 130 }}>
          {toOptions.map(m => <Option key={m} value={m}>{fmtMonth(m)}</Option>)}
        </Select>

        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
      </div>
      )}

      <Spin spinning={loading}>
        {!searched ? (
          <div style={{ color: 'var(--ant-color-text-tertiary)', padding: '24px 0' }}>
            Select a date range and click Search.
          </div>
        ) : (
          <>
            {/* ── Summary cards ── */}
            {show('summary') && (
            <Row gutter={[8, 8]} style={{ marginBottom: 24 }}>
              {/* Eight cards fit a row at sm={3}, so the two headline figures
                  plus all six tenure buckets fill the first line and the
                  voluntary/involuntary split sits on the second. */}
              {[
                { title: 'Total Terms',      value: summary.total,      pct: null,                color: undefined },
                { title: 'Avg Tenure',       value: summary.avgTenure,  pct: null,                color: undefined, suffix: 'd' },
                { title: 'Terms 0-18d',      value: summary.t0to18,    pct: summary.t0to18,    color: '#ff4d4f' },
                { title: 'Terms 19-60d',     value: summary.t19to60,   pct: summary.t19to60,   color: '#fa8c16' },
                { title: 'Terms 61-180d',    value: summary.t61to180,  pct: summary.t61to180,  color: '#faad14' },
                { title: 'Terms 181d-1yr',   value: summary.t181to365, pct: summary.t181to365, color: '#52c41a' },
                { title: 'Terms 1yr-2yr',    value: summary.t366to730, pct: summary.t366to730, color: '#13c2c2' },
                { title: 'Terms 2yr+',       value: summary.t731plus,  pct: summary.t731plus,  color: '#722ed1' },
                { title: 'Voluntary',        value: summary.voluntary,  pct: summary.voluntary,   color: '#52c41a' },
                { title: 'Involuntary',      value: summary.involuntary,pct: summary.involuntary, color: '#ff4d4f' },
              ].map(({ title, value, pct, color, suffix }) => (
                <Col key={title} xs={12} sm={3}>
                  <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                    <Statistic
                      title={<span style={{ fontSize: 12 }}>{title}</span>}
                      value={value}
                      valueStyle={{ color, fontSize: 20 }}
                      suffix={
                        suffix ? <span style={{ fontSize: 12 }}>{suffix}</span>
                        : (pct != null && summary.total)
                          ? <span style={{ fontSize: 12 }}>{Math.round(pct / summary.total * 100)}%</span>
                          : null
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
            )}

            {/* ── By-team pivot ── */}
            {show('summary') && pivotRows.length > 0 && (
              <>
                <Title level={5} style={{ marginBottom: 10 }}>By Team</Title>
                <Table
                  rowKey="TeamID"
                  dataSource={pivotRows}
                  columns={pivotColumns}
                  size="small"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              </>
            )}

            {/* ── Detail table ── */}
            {show('detail') && detailRows.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <Title level={5} style={{ marginBottom: 10 }}>Detail</Title>
                <Table
                  rowKey="_key"
                  dataSource={detailRows}
                  columns={detailColumns}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  rowClassName={r => r._isTotal ? 'attrition-total-row' : ''}
                />
              </>
            )}

            {/* ── Term Reason table ── */}
            {show('reason') && reasonRows.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <Title level={5} style={{ marginBottom: 10 }}>Term Reason</Title>
                <Table
                  rowKey="_key"
                  dataSource={reasonRows}
                  columns={reasonColumns}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  rowClassName={r => r._isTotal ? 'attrition-total-row' : ''}
                />
              </>
            )}

            {/* ── Term Reason bar chart ── */}
            {reasonChartData.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <Title level={5} style={{ marginBottom: 16 }}>Terms by Reason</Title>
                <div style={{ maxWidth: 680 }}>
                  {reasonChartData.map(({ reason, total }) => {
                    const max = reasonChartData[0].total;
                    const pct = max ? (total / max) * 100 : 0;
                    return (
                      <div key={reason} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                        <div style={{ width: 180, textAlign: 'right', fontSize: 12, flexShrink: 0,
                                      color: 'var(--ant-color-text)', overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                             title={reason}>
                          {reason}
                        </div>
                        <div style={{ flex: 1, background: 'var(--ant-color-fill-tertiary)',
                                      borderRadius: 3, height: 20, position: 'relative', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, minWidth: pct > 0 ? 4 : 0,
                                        background: '#4096ff', height: '100%', borderRadius: 3 }} />
                        </div>
                        <div style={{ width: 36, fontSize: 12, fontWeight: 600,
                                      color: 'var(--ant-color-text)', flexShrink: 0, textAlign: 'right' }}>
                          {total}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {/* ── Employee Attrition List ── */}
            {show('list') && listRows.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <Title level={5} style={{ marginBottom: 10 }}>Employee Attrition List</Title>
                <Table
                  rowKey="_key"
                  dataSource={listRows}
                  columns={listColumns}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                />
              </>
            )}
            <style>{`.attrition-total-row td { background: var(--ant-color-fill-secondary) !important; font-weight: 600; }`}</style>

            {searched && !loading && data.length === 0 && (
              <div style={{ color: 'var(--ant-color-text-tertiary)', padding: '24px 0' }}>
                No terminations found for the selected period.
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );
}
